import { defineMessages } from "../i18n/core";

export const machineDetailMessages = defineMessages({
  "pt-BR": {
    backupSent: (host: string) => `Backup de ${host} enviado ao agente.`,
    backupFailed: "Não foi possível iniciar o backup.",
    policyChanged: "Política alterada. O agente recebe a nova configuração em segundos.",
    policyChangeFailed: "Não foi possível alterar a política.",
    revoked: (host: string) => `${host} revogada. Os snapshots continuam disponíveis para restauração.`,
    revokeFailed: "Não foi possível revogar.",
    machines: "Máquinas",
    noGroup: "sem grupo",
    agentOnline: "agente online",
    seen: (when: string) => `visto ${when}`,
    backupNow: "Fazer backup agora",
    noSnapshotToRestore: "Nenhum snapshot para restaurar",
    restoreFiles: "Restaurar arquivos",
    lastRunFailed: "A última execução falhou.",
    neverCompleted: "Esta máquina ainda não concluiu nenhum backup.",
    backupOverdue: "Backup atrasado.",
    restorePoints: "Pontos de restauração",
    last30WithRpo: (hours: number) => `Últimos 30 dias · RPO da política: ${hours} h`,
    last30: "Últimos 30 dias",
    detailsTabs: "Detalhes da máquina",
    tabSnapshots: "Snapshots",
    tabActivity: "Atividade",
    tabAgent: "Detalhes do agente",
    noSnapshots: "Nenhum snapshot ainda.",
    colRestorePoint: "Ponto de restauração",
    colTotal: "Total",
    colNewData: "Dados novos",
    colKeptBy: "Mantido por",
    colImmutableUntil: "Imutável até",
    colActions: "Ações",
    snapshotWhen: (day: string, time: string) => `${day}, ${time}`,
    triggerManual: "manual",
    triggerSchedule: "agendado",
    partial: "parcial",
    partialWhat: (n: number) => `O que ficou de fora (${n} ${n === 1 ? "exemplo" : "exemplos"})`,
    partialReport: (total: number, files: number) => `Ponto parcial: ${total} ${total === 1 ? "item ficou" : "itens ficaram"} de fora; ${files} ${files === 1 ? "arquivo copiado" : "arquivos copiados"}.`,
    partialGroupsTitle: "Onde e por quê",
    partialColDir: "Pasta",
    partialColError: "Erro",
    partialColCount: "Itens",
    partialOtherDirs: "outras pastas",
    partialRoot: "(raiz da origem)",
    partialListTitle: (listed: number, total: number) => listed < total ? `Ver a lista (os primeiros ${listed} de ${total})` : `Ver a lista (${listed})`,
    partialListLoading: "Carregando a lista…",
    partialListFailed: "Não foi possível carregar a lista.",
    partialListEmpty: "Este ponto é anterior ao relatório detalhado: a lista só existe a partir do próximo backup.",
    partialCsv: "Baixar CSV",
    restore: "Restaurar",
    noActivity: "Sem atividade recente.",
    system: "Sistema",
    agent: "Agente",
    address: "Endereço",
    enrolledAt: "Registrada em",
    certValidUntil: "Certificado válido até",
    autoRenewal: "(renovação automática)",
    identifier: "Identificador",
    sources: "Fontes",
    sourcesFromPolicy: "definidas pela política",
    volume: "Volume",
    protected: "protegido",
    stored: "armazenado",
    dedup: "deduplicação",
    policy: "Política",
    edit: "Editar",
    name: "Nome",
    machinePolicy: "Política da máquina",
    retention: "Retenção",
    totalSnapshots: "Snapshots totais",
    dangerZone: "Zona de risco",
    imageNow: "Fazer imagem agora",
    imageSent: (host: string) => `Imagem de ${host} enviada ao agente.`,
    imageFailed: "Não foi possível iniciar a imagem.",
    tabImages: "Imagens",
    tabDatabases: "Bancos de dados",
    tabShares: "Compartilhamentos",
    disks: "Discos",
    disksHint: "Informados pelo agente. Marque os que entram na imagem: a marcação fica salva e segue a agenda da política.",
    disksScheduled: (policy: string, n: number) => `Imagem na agenda da política ${policy}: ${n} ${n === 1 ? "disco marcado" : "discos marcados"}.`,
    disksPolicyDefault: (policy: string) => `A política ${policy} copia os discos de sistema na agenda; marque para escolher outros.`,
    disksNotScheduled: "Nenhum disco marcado: esta máquina não faz imagem pela agenda. Marque um disco, ou ligue a imagem na política.",
    disksSaved: "Discos salvos. O agente recebe a mudança no próximo contato.",
    disksSaveFailed: "Não foi possível salvar os discos.",
    disksNone: "Esta máquina não faz backup de imagem. No macOS o produto copia apenas arquivos.",
    disksStale: (when: string) => `inventário de ${when}`,
    diskSize: (size: string) => `${size} no total`,
    diskSystem: "disco de sistema",
    noImages: "Nenhuma imagem de disco ainda.",
    colImage: "Imagem",
    colVolume: "Volume",
    colUsed: "Usado",
    colConsistency: "Consistência",
    consistency: { vss: "VSS", lvm: "LVM", crash: "por queda" },
    consistencyCrashHint: "Imagem consistente por queda, como um desligamento abrupto: o sistema de arquivos monta com recuperação do diário.",
    downloadVolume: "Baixar",
    downloadStarted: "Download iniciado. O volume é grande e pode levar bastante tempo.",
    downloadLayout: "Baixar layout do disco",
    layoutHint: "Tabela de partição e setor de boot. Sem ele, o volume bruto não volta para um disco.",
    revokeExplain: "Revogar invalida o certificado do agente imediatamente. Os snapshots existentes são mantidos e podem ser restaurados.",
    revokeMachine: "Revogar máquina",
    revokeTitle: (host: string) => `Revogar ${host}?`,
    revokeDescription: "O agente perde o acesso na próxima verificação, em poucos segundos. Para voltar a proteger esta máquina será preciso registrá-la de novo.",
  },
  "en-US": {
    backupSent: (host: string) => `Backup of ${host} sent to the agent.`,
    backupFailed: "Could not start the backup.",
    policyChanged: "Policy changed. The agent receives the new configuration within seconds.",
    policyChangeFailed: "Could not change the policy.",
    revoked: (host: string) => `${host} revoked. Its snapshots remain available for restore.`,
    revokeFailed: "Could not revoke.",
    machines: "Machines",
    noGroup: "no group",
    agentOnline: "agent online",
    seen: (when: string) => `seen ${when}`,
    backupNow: "Back up now",
    noSnapshotToRestore: "No snapshot to restore",
    restoreFiles: "Restore files",
    lastRunFailed: "The last run failed.",
    neverCompleted: "This machine has not completed a backup yet.",
    backupOverdue: "Backup overdue.",
    restorePoints: "Restore points",
    last30WithRpo: (hours: number) => `Last 30 days · Policy RPO: ${hours} h`,
    last30: "Last 30 days",
    detailsTabs: "Machine details",
    tabSnapshots: "Snapshots",
    tabActivity: "Activity",
    tabAgent: "Agent details",
    noSnapshots: "No snapshots yet.",
    colRestorePoint: "Restore point",
    colTotal: "Total",
    colNewData: "New data",
    colKeptBy: "Kept by",
    colImmutableUntil: "Immutable until",
    colActions: "Actions",
    snapshotWhen: (day: string, time: string) => `${day}, ${time}`,
    triggerManual: "manual",
    triggerSchedule: "scheduled",
    partial: "partial",
    partialWhat: (n: number) => `What was left out (${n} ${n === 1 ? "example" : "examples"})`,
    partialReport: (total: number, files: number) => `Partial point: ${total} ${total === 1 ? "item was" : "items were"} left out; ${files} ${files === 1 ? "file copied" : "files copied"}.`,
    partialGroupsTitle: "Where and why",
    partialColDir: "Folder",
    partialColError: "Error",
    partialColCount: "Items",
    partialOtherDirs: "other folders",
    partialRoot: "(source root)",
    partialListTitle: (listed: number, total: number) => listed < total ? `See the list (first ${listed} of ${total})` : `See the list (${listed})`,
    partialListLoading: "Loading the list…",
    partialListFailed: "The list could not be loaded.",
    partialListEmpty: "This point predates the detailed report: the list only exists from the next backup on.",
    partialCsv: "Download CSV",
    restore: "Restore",
    noActivity: "No recent activity.",
    system: "System",
    agent: "Agent",
    address: "Address",
    enrolledAt: "Enrolled on",
    certValidUntil: "Certificate valid until",
    autoRenewal: "(renews automatically)",
    identifier: "Identifier",
    sources: "Sources",
    sourcesFromPolicy: "defined by the policy",
    volume: "Volume",
    protected: "protected",
    stored: "stored",
    dedup: "deduplication",
    policy: "Policy",
    edit: "Edit",
    name: "Name",
    machinePolicy: "Machine policy",
    retention: "Retention",
    totalSnapshots: "Total snapshots",
    dangerZone: "Danger zone",
    imageNow: "Image now",
    imageSent: (host: string) => `Disk image of ${host} sent to the agent.`,
    imageFailed: "Could not start the disk image.",
    tabImages: "Images",
    tabDatabases: "Databases",
    tabShares: "Shares",
    disks: "Disks",
    disksHint: "Reported by the agent. Check the ones that go into the image: the selection is saved and follows the policy's schedule.",
    disksScheduled: (policy: string, n: number) => `Image scheduled by policy ${policy}: ${n} ${n === 1 ? "disk selected" : "disks selected"}.`,
    disksPolicyDefault: (policy: string) => `Policy ${policy} copies the system disks on schedule; select disks to choose others.`,
    disksNotScheduled: "No disk selected: this machine does not take images on schedule. Select a disk, or turn images on in the policy.",
    disksSaved: "Disks saved. The agent gets the change on its next contact.",
    disksSaveFailed: "The disks could not be saved.",
    disksNone: "This machine does not do image backup. On macOS the product copies files only.",
    disksStale: (when: string) => `inventory from ${when}`,
    diskSize: (size: string) => `${size} total`,
    diskSystem: "system disk",
    noImages: "No disk image yet.",
    colImage: "Image",
    colVolume: "Volume",
    colUsed: "Used",
    colConsistency: "Consistency",
    consistency: { vss: "VSS", lvm: "LVM", crash: "crash" },
    consistencyCrashHint: "Crash-consistent image, like an abrupt shutdown: the filesystem mounts with journal recovery.",
    downloadVolume: "Download",
    downloadStarted: "Download started. The volume is large and may take a long while.",
    downloadLayout: "Download disk layout",
    layoutHint: "Partition table and boot sector. Without it, the raw volume cannot be put back on a disk.",
    revokeExplain: "Revoking invalidates the agent certificate immediately. Existing snapshots are kept and can be restored.",
    revokeMachine: "Revoke machine",
    revokeTitle: (host: string) => `Revoke ${host}?`,
    revokeDescription: "The agent loses access at its next check, within a few seconds. To protect this machine again you will need to enroll it again.",
  },
  es: {
    backupSent: (host: string) => `Copia de seguridad de ${host} enviada al agente.`,
    backupFailed: "No se pudo iniciar la copia de seguridad.",
    policyChanged: "Política cambiada. El agente recibe la nueva configuración en segundos.",
    policyChangeFailed: "No se pudo cambiar la política.",
    revoked: (host: string) => `${host} revocado. Los snapshots siguen disponibles para restaurar.`,
    revokeFailed: "No se pudo revocar.",
    machines: "Equipos",
    noGroup: "sin grupo",
    agentOnline: "agente en línea",
    seen: (when: string) => `visto ${when}`,
    backupNow: "Hacer copia ahora",
    noSnapshotToRestore: "No hay snapshots para restaurar",
    restoreFiles: "Restaurar archivos",
    lastRunFailed: "La última ejecución falló.",
    neverCompleted: "Este equipo todavía no completó ninguna copia de seguridad.",
    backupOverdue: "Copia de seguridad atrasada.",
    restorePoints: "Puntos de restauración",
    last30WithRpo: (hours: number) => `Últimos 30 días · RPO de la política: ${hours} h`,
    last30: "Últimos 30 días",
    detailsTabs: "Detalles del equipo",
    tabSnapshots: "Snapshots",
    tabActivity: "Actividad",
    tabAgent: "Detalles del agente",
    noSnapshots: "Todavía no hay snapshots.",
    colRestorePoint: "Punto de restauración",
    colTotal: "Total",
    colNewData: "Datos nuevos",
    colKeptBy: "Conservado por",
    colImmutableUntil: "Inmutable hasta",
    colActions: "Acciones",
    snapshotWhen: (day: string, time: string) => `${day}, ${time}`,
    triggerManual: "manual",
    triggerSchedule: "programado",
    partial: "parcial",
    partialWhat: (n: number) => `Lo que quedó fuera (${n} ${n === 1 ? "ejemplo" : "ejemplos"})`,
    partialReport: (total: number, files: number) => `Punto parcial: ${total} ${total === 1 ? "elemento quedó" : "elementos quedaron"} fuera; ${files} ${files === 1 ? "archivo copiado" : "archivos copiados"}.`,
    partialGroupsTitle: "Dónde y por qué",
    partialColDir: "Carpeta",
    partialColError: "Error",
    partialColCount: "Elementos",
    partialOtherDirs: "otras carpetas",
    partialRoot: "(raíz del origen)",
    partialListTitle: (listed: number, total: number) => listed < total ? `Ver la lista (los primeros ${listed} de ${total})` : `Ver la lista (${listed})`,
    partialListLoading: "Cargando la lista…",
    partialListFailed: "No se pudo cargar la lista.",
    partialListEmpty: "Este punto es anterior al informe detallado: la lista solo existe a partir de la próxima copia.",
    partialCsv: "Descargar CSV",
    restore: "Restaurar",
    noActivity: "Sin actividad reciente.",
    system: "Sistema",
    agent: "Agente",
    address: "Dirección",
    enrolledAt: "Registrado el",
    certValidUntil: "Certificado válido hasta",
    autoRenewal: "(renovación automática)",
    identifier: "Identificador",
    sources: "Orígenes",
    sourcesFromPolicy: "definidos por la política",
    volume: "Volumen",
    protected: "protegido",
    stored: "almacenado",
    dedup: "deduplicación",
    policy: "Política",
    edit: "Editar",
    name: "Nombre",
    machinePolicy: "Política del equipo",
    retention: "Retención",
    totalSnapshots: "Snapshots totales",
    dangerZone: "Zona de riesgo",
    imageNow: "Crear imagen ahora",
    imageSent: (host: string) => `Imagen de ${host} enviada al agente.`,
    imageFailed: "No se pudo iniciar la imagen.",
    tabImages: "Imágenes",
    tabDatabases: "Bases de datos",
    tabShares: "Recursos compartidos",
    disks: "Discos",
    disksHint: "Informados por el agente. Marca los que entran en la imagen: la selección se guarda y sigue la programación de la política.",
    disksScheduled: (policy: string, n: number) => `Imagen programada por la política ${policy}: ${n} ${n === 1 ? "disco marcado" : "discos marcados"}.`,
    disksPolicyDefault: (policy: string) => `La política ${policy} copia los discos de sistema según la programación; marca discos para elegir otros.`,
    disksNotScheduled: "Ningún disco marcado: este equipo no hace imagen por programación. Marca un disco, o activa la imagen en la política.",
    disksSaved: "Discos guardados. El agente recibe el cambio en el próximo contacto.",
    disksSaveFailed: "No se pudieron guardar los discos.",
    disksNone: "Este equipo no hace copia de imagen. En macOS el producto copia solo archivos.",
    disksStale: (when: string) => `inventario de ${when}`,
    diskSize: (size: string) => `${size} en total`,
    diskSystem: "disco de sistema",
    noImages: "Todavía no hay ninguna imagen de disco.",
    colImage: "Imagen",
    colVolume: "Volumen",
    colUsed: "Usado",
    colConsistency: "Consistencia",
    consistency: { vss: "VSS", lvm: "LVM", crash: "por caída" },
    consistencyCrashHint: "Imagen consistente por caída, como un apagado abrupto: el sistema de archivos monta con recuperación del diario.",
    downloadVolume: "Descargar",
    downloadStarted: "Descarga iniciada. El volumen es grande y puede tardar bastante.",
    downloadLayout: "Descargar el diseño del disco",
    layoutHint: "Tabla de particiones y sector de arranque. Sin él, el volumen sin procesar no vuelve a un disco.",
    revokeExplain: "Revocar invalida el certificado del agente de inmediato. Los snapshots existentes se conservan y se pueden restaurar.",
    revokeMachine: "Revocar equipo",
    revokeTitle: (host: string) => `¿Revocar ${host}?`,
    revokeDescription: "El agente pierde el acceso en la próxima verificación, en pocos segundos. Para volver a proteger este equipo tendrás que registrarlo de nuevo.",
  },
});
